/* Copyright Levelise Ltd 2019 - 2026 */
import { useContext, useEffect } from 'react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
	Chart,
	ArcElement,
	LineElement,
	BarElement,
	PointElement,
	BarController,
	BubbleController,
	DoughnutController,
	LineController,
	PieController,
	PolarAreaController,
	RadarController,
	ScatterController,
	CategoryScale,
	LinearScale,
	LogarithmicScale,
	RadialLinearScale,
	TimeScale,
	TimeSeriesScale,
	Decimation,
	Filler,
	Legend,
	Title,
	Tooltip,
	SubTitle,
} from 'chart.js';

import IdleService from '../../services/idle-service';
import TokenService from '../../services/token-service';
import UserService from '../../services/user-service';

import { combineStatus, detectBrowser, hasPermission } from '../../utils/utils';
import { verticalLinePlugin } from '../../utils/chart';
import { PERMISSIONS } from '../../utils/constants';

import { FacilityProvider } from '../../contexts/FacilityContext';
import FleetContext from '../../contexts/FleetContext';
import FleetStatusContext from '../../contexts/FleetStatusContext';
import OrdersContext from '../../contexts/OrdersContext';
import UserContext from '../../contexts/UserContext';
import { NavBarProvider } from '../../contexts/NavBarContext';

import NavBar from '../NavBar';

import './App.css';
import { CustomerProvider } from '../../contexts/CustomerContext';
import FleetService from '../../services/fleet-service';
import ErrorBoundary from '../ErrorBoundary';
import RouteLoadingFallback from '../RouteLoadingFallback';

// Routes (lazy-loaded for code splitting)
import { lazy, Suspense } from 'react';
const AddCustomerRoute = lazy(() => import('../../routes/AddCustomerRoute'));
const AuthRoute = lazy(() => import('../../routes/AuthRoute/index'));
const ConfigurationRoute = lazy(() => import('../../routes/ConfigureRoute/index'));
const CustomerRoute = lazy(() => import('../../routes/CustomerRoute'));
const CustomersRoute = lazy(() => import('../../routes/CustomersRoute'));
const ErrorRoute = lazy(() => import('../../routes/ErrorRoute'));
const FacilityRoute = lazy(() => import('../../routes/FacilityRoute/index'));
const FleetRoute = lazy(() => import('../../routes/FleetRoute/index'));
const OrdersRoute = lazy(() => import('../../routes/OrdersRoute'));
const Route404 = lazy(() => import('../../routes/404Route/index'));
const UserRoute = lazy(() => import('../../routes/UserRoute'));

Chart.register(
	ArcElement,
	LineElement,
	BarElement,
	PointElement,
	BarController,
	BubbleController,
	DoughnutController,
	LineController,
	PieController,
	PolarAreaController,
	RadarController,
	ScatterController,
	CategoryScale,
	LinearScale,
	LogarithmicScale,
	RadialLinearScale,
	TimeScale,
	TimeSeriesScale,
	Decimation,
	Filler,
	Legend,
	Title,
	Tooltip,
	SubTitle,
	annotationPlugin,
	zoomPlugin,
	verticalLinePlugin,
);

// Expose the Chart registry so E2E tests can assert on chart internals (axis ranges, dataset
// values) via Chart.getChart(canvasId). Guarded so it never reaches a production bundle.
// eslint-disable-next-line no-undef
if (process.env.NODE_ENV !== 'production') {
	window.Chart = Chart;
}

const UserAccessedRoute = ({ children }) => {
	return UserService.hasUser() ? children : <Navigate to="/auth" />;
};

const ProviderRoute = ({ children }) => {
	return UserService.hasUser() ? children : <Navigate to="/auth" />;
};

// UserService.isAdmin()
const AdminRoute = ({ children }) => {
	if (hasPermission(PERMISSIONS.CAN_CREATE_FACILITY)) {
		return children;
	}

	return <Navigate to="/auth" />;
};

const CustomerAccessRoute = ({ children }) => {
	if (hasPermission(PERMISSIONS.CAN_ACCESS_CUSTOMER)) {
		return children;
	}

	return <Navigate to="/auth" />;
};

const OrdersAccessRoute = ({ children }) => {
	if (hasPermission(PERMISSIONS.CAN_ACCESS_ORDERS)) {
		return children;
	}

	return <Navigate to="/auth" />;
};

const App = () => {
	const fleetContext = useContext(FleetContext);
	const fleetStatusContext = useContext(FleetStatusContext);
	const ordersContext = useContext(OrdersContext);
	const userContext = useContext(UserContext);

	const navigate = useNavigate();

	useEffect(() => {
		return () => {
			IdleService.unRegisterIdleResets();
			TokenService.clearAuthToken();
			UserService.removeUser();
			UserService.removeUserDetail();
			TokenService.clearCallbackBeforeExpiry();
		};
	}, []);

	useEffect(() => {
		function refreshTokenAndScheduleRefresh() {
			TokenService.refreshToken()
				.then(() => {
					IdleService.setIdleCallback(() => {
						userContext.clearUser();
						fleetContext.clearState();
						fleetStatusContext.clearState();
						ordersContext.clearState();
						IdleService.unRegisterIdleResets();
						UserService.removePreferences();
						UserService.removeUser();
						TokenService.clearAuthToken();
						TokenService.clearCallbackBeforeExpiry();
						navigate('/auth');
					});

					IdleService.resetIdleTimer();

					IdleService.registerIdleTimerResets();
				})
				.catch((error) => {
					console.log('Error on refresh: ', error);
				});
		}

		if (TokenService.getAuthToken()) {
			refreshTokenAndScheduleRefresh();
		}
	}, []);

	useEffect(() => {
		let interval = null;
		let username = userContext.username || UserService.getUsername();
		if (username) {
			FleetService.getUserFacilities(username)
				.then((res) => {
					const facilities = res && Object.hasOwn(res, 'facilities') ? res.facilities : [];
					const facilitiesSortedByDruId = JSON.parse(JSON.stringify(facilities));
					fleetContext.setAllFacilities(facilities);
					facilitiesSortedByDruId.sort((a, b) => a.druId - b.druId);
					fleetContext.setFacilitiesSortedByDruId(facilitiesSortedByDruId);
				})
				.catch(fleetContext.setError);

			FleetService.getDruStatuses(username)
				.then((druStatus) => {
					const [drus, status] = combineStatus(druStatus);
					fleetContext.setStatus(status);
					fleetContext.setCurrentDrus(drus);
				})
				.catch(fleetContext.setError);

			clearInterval(interval);
			interval = setInterval(
				() => {
					FleetService.getDruStatuses(username)
						.then((druStatus) => {
							const [drus, status] = combineStatus(druStatus);
							fleetContext.setStatus(status);
							fleetContext.setCurrentDrus(drus);
						})
						.catch(fleetContext.setError);
				},
				10 * 60 * 1000,
			);
		}

		return () => clearInterval(interval);
	}, [userContext.username]);

	return (
		<div className="App" id={detectBrowser()}>
			<NavBarProvider>
				<NavBar />
				<ErrorBoundary>
					<Suspense fallback={<RouteLoadingFallback />}>
					<Routes>
						<Route path="/" element={<Navigate replace to="/auth" />} />
						<Route path="/auth" element={<AuthRoute />} />
						<Route
							path="/fleet"
							element={
								<ProviderRoute>
									<FleetRoute />
								</ProviderRoute>
							}
						/>
						<Route
							path="/customers"
							element={
								<CustomerAccessRoute>
									<CustomersRoute />
								</CustomerAccessRoute>
							}
						/>
						<Route
							path="/orders"
							element={
								<OrdersAccessRoute>
									<OrdersRoute />
								</OrdersAccessRoute>
							}
						/>
						<Route
							path="/customer/:customerId"
							element={
								<CustomerAccessRoute>
									<CustomerProvider>
										<CustomerRoute />
									</CustomerProvider>
								</CustomerAccessRoute>
							}
						/>
						<Route
							path="/add-customer"
							element={
								<CustomerAccessRoute>
									<AddCustomerRoute />
								</CustomerAccessRoute>
							}
						/>
						<Route
							path="/facility/:facilityName"
							element={
								<UserAccessedRoute>
									<FacilityProvider>
										<FacilityRoute />
									</FacilityProvider>
								</UserAccessedRoute>
							}
						/>
						<Route
							path="/user"
							element={
								<UserAccessedRoute>
									<UserRoute />
								</UserAccessedRoute>
							}
						/>
						<Route
							path="/configure-facility"
							element={
								<AdminRoute>
									<ConfigurationRoute />
								</AdminRoute>
							}
						/>
						<Route path="/error-page" element={<ErrorRoute />} />
						<Route path="*" element={<Route404 />} />
					</Routes>
				</Suspense>
			</ErrorBoundary>
			</NavBarProvider>
		</div>
	);
};

export default App;
